import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Checkbox from '../../../core/components/forms/Checkbox';
import SellTicketsLayout from '../SellTicketsLayout';
import FloatingLabelTextInput from '../../../core/components/forms/FloatingLabelTextInput';
import SellTicketsHeader from '../SellTicketsHeader';
import withAsyncLifecycle from '../../../core/hoc/async-lifecycle';
import { selectSellFormData } from '../../selectors/sell-tickets';
import { setFieldValue } from '../../actions/forms';
import { selectHttpInfo } from '../../../core/selectors/http';
import { getBankAccount, saveBankAccount, SELL_GET_TICKETS_REQUEST_NAME } from '../../actions/sell-tickets';
import {
    BANK_ACCOUNT_BIC_FIELD, BANK_ACCOUNT_FORM, BANK_ACCOUNT_FUTURE_USE_FIELD, BANK_ACCOUNT_IBAN_FIELD,
    BANK_ACCOUNT_NAME_FIELD,
} from '../../reducers/features';
import { SELL_STEP_5 } from '../../constants/steps';

const SellStep4 = () => {
    const dispatch = useDispatch();
    const { isFetching, isError } = useSelector((state) => selectHttpInfo(state, SELL_GET_TICKETS_REQUEST_NAME));

    useEffect(() => {
        dispatch(getBankAccount());
    }, []);

    const SellStep4WithAsyncLifecycle = withAsyncLifecycle(SellStep4Content, { isFetching, isError });
    return <SellStep4WithAsyncLifecycle />;
};

const SellStep4Content = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const intl = useIntl();

    const inputs = useSelector((state) => selectSellFormData(state, BANK_ACCOUNT_FORM));
    const goToNextStep = () => navigate(`/sell/${SELL_STEP_5}`);

    return (
        <SellTicketsLayout>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <SellTicketsHeader
                        title={<FormattedMessage id="sell_add_bank_account_info"/>}
                        subtitle={<FormattedMessage id="sell_add_bank_account_desc"/>}
                    />
                    <div className="border rounded mb-3 p-3">
                        <p className="mb-3"><FormattedMessage id="sell_add_bank_account_desc_form"/></p>
                        <Row>
                            <Col lg={6} md={12}>
                                <FloatingLabelTextInput
                                    controlId={BANK_ACCOUNT_IBAN_FIELD}
                                    value={inputs[BANK_ACCOUNT_IBAN_FIELD].value}
                                    label="IBAN"
                                    isInvalid={!inputs[BANK_ACCOUNT_IBAN_FIELD].isValid}
                                    errorMessage={inputs[BANK_ACCOUNT_IBAN_FIELD].errorMessage}
                                    onChange={(e) => {
                                        const strictText = e.target.value.replace(/\s+/g, '');
                                        dispatch(setFieldValue(BANK_ACCOUNT_FORM, BANK_ACCOUNT_IBAN_FIELD, strictText));
                                    }}
                                />
                                {inputs[BANK_ACCOUNT_BIC_FIELD].isVisible && (
                                    <FloatingLabelTextInput
                                        controlId={BANK_ACCOUNT_BIC_FIELD}
                                        value={inputs[BANK_ACCOUNT_BIC_FIELD].value}
                                        label="BIC/SWIFT code"
                                        isInvalid={!inputs[BANK_ACCOUNT_BIC_FIELD].isValid}
                                        errorMessage={inputs[BANK_ACCOUNT_BIC_FIELD].errorMessage}
                                        onChange={(e) => {
                                            dispatch(setFieldValue(BANK_ACCOUNT_FORM, BANK_ACCOUNT_BIC_FIELD, e.target.value));
                                        }}
                                    />
                                )}
                                <FloatingLabelTextInput
                                    controlId={BANK_ACCOUNT_NAME_FIELD}
                                    value={inputs[BANK_ACCOUNT_NAME_FIELD].value}
                                    label={<FormattedMessage id="sell_bank_account_name"/>}
                                    isInvalid={!inputs[BANK_ACCOUNT_NAME_FIELD].isValid}
                                    errorMessage={inputs[BANK_ACCOUNT_NAME_FIELD].errorMessage}
                                    onChange={(e) => {
                                        dispatch(setFieldValue(BANK_ACCOUNT_FORM, BANK_ACCOUNT_NAME_FIELD, e.target.value));
                                    }}
                                />
                                <Checkbox
                                    controlId={BANK_ACCOUNT_FUTURE_USE_FIELD}
                                    label={<FormattedMessage id="sell_bank_account_future_use"/>}
                                    checked={inputs[BANK_ACCOUNT_FUTURE_USE_FIELD].value}
                                    isInvalid={!inputs[BANK_ACCOUNT_FUTURE_USE_FIELD].isValid}
                                    onChange={(e) => {
                                        dispatch(setFieldValue(BANK_ACCOUNT_FORM, BANK_ACCOUNT_FUTURE_USE_FIELD, e.target.checked));
                                    }}/>
                                {inputs[BANK_ACCOUNT_FUTURE_USE_FIELD].errorMessage
                                    && <span className="text-danger">{inputs[BANK_ACCOUNT_FUTURE_USE_FIELD].errorMessage}</span>
                                }
                            </Col>
                            <Col lg={6} md={12}>
                                <div className="bank-account-alert border rounded">
                                    <div className="bank-account-alert__image rounded-circle"><span /></div>
                                    <div className=""><FormattedMessage id="sell_bank_account_alert"/></div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <div className="step-actions d-flex mt-3 mb-5">
                        <Button variant="link" className="text-decoration-none me-2" onClick={() => navigate('/sell/3')}>
                            <FormattedMessage id="prev"/>
                        </Button>
                        <Button variant="primary" size="lg" onClick={() => dispatch(saveBankAccount(inputs, goToNextStep, intl))}>
                            <FormattedMessage id="continue"/>
                        </Button>
                    </div>
                </Col>
            </Row>
        </SellTicketsLayout>
    );
};
export default SellStep4;
